/**
 * First-run tutorial: build a parry spell, then use it.
 *
 * Runs entirely offline against a local simulation, so a brand-new player never
 * has to find an opponent first. The judging code is the *same* code the server
 * runs — `scoreAttempt`, `chantSimilarity`, `combineScores`, `resolveParryWindow`
 * — with one deliberate difference: the first attempt gets extra grace, because
 * the point of the first attempt is to feel the moment, not to be tested.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  chantSimilarity,
  combineScores,
  parryResolveHoldMs,
  resolveParryWindow,
} from '@spell/combat-rules';
import { scoreAttempt } from '@spell/gesture-engine';
import { normalizeChant, type Spell } from '@spell/spell-schema';
import { COMBAT, TIMING, makeId } from '@spell/shared-types';
import { SpellCraft, type CraftResult } from '../crafting/SpellCraft';
import { buildSpellLocally } from '../crafting/buildSpell';
import { ParryTiming } from '../ui/ParryTiming';
import { useFlow } from '../gameplay/flowStore';
import {
  kickShake,
  pushImpact,
  resetScene,
  scene as bus,
  triggerHitStop,
  type ProjectileView,
} from '../scene/sceneBus';
import { combatRecorder, gestureTrail, syncTrailFrom } from '../gesture/capture';
import { chantRecognizer, mic, speechSupported } from '../voice/mic';
import { sfx } from '../audio/sfx';
import { SpellNet } from '../networking/net';

type Stage =
  | 'intro'
  | 'incoming'
  | 'frozen'
  | 'craft'
  | 'ready'
  | 'attempt1'
  | 'success1'
  | 'attempt2'
  | 'done';

const DEFAULTS = {
  chant: 'Mirror Break',
  description: '공격을 산산조각 내고 일부를 상대에게 돌려보낸다.',
};

interface Attempt {
  grade: string;
  gesture: number;
  chant: number;
  timingError: number;
  success: boolean;
  reason?: string;
}

export function TutorialScreen() {
  const completeTutorial = useFlow((s) => s.completeTutorial);
  const [stage, setStage] = useState<Stage>('intro');
  const [spell, setSpell] = useState<Spell | null>(null);
  const [notes, setNotes] = useState<string[]>([]);
  const [attempt, setAttempt] = useState<Attempt | null>(null);
  const [building, setBuilding] = useState(false);
  const [typedChant, setTypedChant] = useState('');
  const [useTyping, setUseTyping] = useState(false);
  const [micReady, setMicReady] = useState(false);

  const stageRef = useRef<Stage>('intro');
  const spellRef = useRef<Spell | null>(null);
  const bolt = useRef<ProjectileView | null>(null);
  const impactAt = useRef(0);
  /** Local-clock deadline of the impact hold; 0 while the bolt is in flight. */
  const resolveAt = useRef(0);
  const casting = useRef(false);
  const castOnset = useRef(0);
  const transcript = useRef('');
  const rafRef = useRef(0);

  stageRef.current = stage;
  spellRef.current = spell;

  /* --- scene setup ---------------------------------------------------- */
  useEffect(() => {
    resetScene('tutorial');
    bus.selfSide = 1;
    bus.oppSide = -1;
    bus.oppPresent = true;
    bus.selfHp = COMBAT.MAX_HP;
    bus.selfMaxHp = COMBAT.MAX_HP;
    bus.oppHp = COMBAT.MAX_HP;
    bus.oppMaxHp = COMBAT.MAX_HP;
    bus.yaw = 0;
    bus.pitch = 0;
    sfx.unlock();
    return () => resetScene('idle');
  }, []);

  /* --- microphone (optional) ------------------------------------------ */
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const ok = await mic.start();
      if (cancelled) return;
      setMicReady(ok);
      if (ok && speechSupported()) chantRecognizer.start();
      else setUseTyping(true);
    })();
    return () => {
      cancelled = true;
      chantRecognizer.stop();
    };
  }, []);

  useEffect(() => {
    const off = chantRecognizer.onUpdate(({ transcript: text }) => {
      if (casting.current) transcript.current = text;
    });
    // The most recent onset inside the cast is the one that counts — see the
    // same rule in useCombatInput.
    const offOnset = mic.onOnset((at) => {
      if (casting.current) castOnset.current = at;
    });
    // If recognition is unavailable or dies, the keyboard takes over rather
    // than leaving a first-time player unable to finish the tutorial.
    const offFailure = chantRecognizer.onFailure(() => setUseTyping(true));
    return () => {
      off();
      offOnset();
      offFailure();
    };
  }, []);

  /* --- the local bolt simulation --------------------------------------- */
  const launchBolt = useCallback((speed: number) => {
    const startZ = bus.oppSide * (COMBAT.DUEL_DISTANCE / 2);
    bolt.current = {
      id: 'tutorial-bolt',
      x: 0,
      y: 1.55,
      z: startZ + 0.9,
      vx: 0,
      vy: 0,
      vz: -Math.sign(bus.oppSide) * speed,
      radius: 0.62,
      color: '#ff4a2c',
      accentColor: '#ffd08a',
      shape: 'orb',
      element: 'fire',
      homing: 0,
      incoming: true,
      parryable: true,
      eta: 0,
      resolveIn: 0,
      armUntil: 0,
      complexity: 2,
    };
    resolveAt.current = 0;
    bus.projectiles = [bolt.current];
    sfx.signature(1.2);
  }, []);

  useEffect(() => {
    let last = performance.now();
    const loop = () => {
      rafRef.current = requestAnimationFrame(loop);
      const now = performance.now();
      const delta = Math.min(0.05, (now - last) / 1000);
      last = now;

      const b = bolt.current;
      const s = stageRef.current;
      if (!b) return;

      const frozen = s === 'frozen' || s === 'craft';
      const holding = resolveAt.current > 0;
      if (!frozen && !holding && bus.hitStop <= 0) {
        b.x += b.vx * delta * bus.timeScale;
        b.y += b.vy * delta * bus.timeScale;
        b.z += b.vz * delta * bus.timeScale;
      }
      if (bus.hitStop > 0) bus.hitStop = Math.max(0, bus.hitStop - delta);

      const targetZ = bus.selfSide * (COMBAT.DUEL_DISTANCE / 2);
      const distance = Math.abs(b.z - targetZ) - COMBAT.PLAYER_HIT_RADIUS - b.radius;
      const speed = Math.abs(b.vz) || 1;
      const eta = Math.max(0, distance / speed);
      b.eta = eta;
      // `impactAt` is frozen once the bolt has arrived: the parry window is
      // judged against it, and letting it drift would slide the window along
      // with the hold. Same rule the server follows in `beginResolve`.
      if (!holding) impactAt.current = SpellNet.now() + eta * 1000;
      b.resolveIn = holding ? Math.max(0, (resolveAt.current - SpellNet.now()) / 1000) : 0;
      bus.danger = Math.max(0, 1 - eta / 1.4);

      // First run: stop time just before impact and hand the player the forge.
      if (s === 'incoming' && eta <= 0.34) {
        setStage('frozen');
        bus.timeScale = 0;
        triggerHitStop(0.2, 0.1, 0);
        sfx.warning();
        window.setTimeout(() => setStage('craft'), 700);
        return;
      }

      // Arrived: hang at the point of impact and stay parryable for the beat,
      // exactly as the server does. The tutorial is where this timing is
      // learned, so it cannot be more or less forgiving than the real thing.
      if ((s === 'attempt1' || s === 'attempt2') && distance <= 0 && !holding) {
        // Offline, so there is no round trip to absorb — the hold is the bare
        // published beat.
        resolveAt.current = SpellNet.now() + parryResolveHoldMs(0);
        b.vx = 0;
        b.vy = 0;
        b.vz = 0;
        return;
      }

      if ((s === 'attempt1' || s === 'attempt2') && holding && SpellNet.now() >= resolveAt.current) {
        // The bolt got through.
        resolveAt.current = 0;
        bolt.current = null;
        bus.projectiles = [];
        bus.selfHp = Math.max(20, bus.selfHp - 14);
        pushImpact({ x: 0, y: 1.5, z: targetZ, color: '#ff4a2c', kind: 'damage', amount: 14, life: 0.7, self: true });
        kickShake(0.9);
        sfx.impact(1.1);
        setAttempt({ grade: 'Fail', gesture: 0, chant: 0, timingError: 0, success: false, reason: 'missed' });
        window.setTimeout(() => {
          bus.selfHp = COMBAT.MAX_HP;
          setAttempt(null);
          launchBolt(s === 'attempt1' ? 8 : 13);
        }, 1400);
      }
    };
    rafRef.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(rafRef.current);
  }, [launchBolt]);

  /* --- cast input ------------------------------------------------------ */
  const judge = useCallback(
    (points: ReturnType<typeof combatRecorder.finish>, spokenAt: number) => {
      const current = spellRef.current;
      const s = stageRef.current;
      if (!current || (s !== 'attempt1' && s !== 'attempt2')) return;

      const gestureScore = current.gesture ? scoreAttempt(points, current.gesture).score : 1;
      // Prefer what was actually heard; fall back to what was typed. Either is
      // a legitimate way to deliver the incantation.
      const spoken = normalizeChant(transcript.current.trim() || typedChant);
      const chantScore = chantSimilarity(spoken, current.normalizedChant);
      const score = combineScores(gestureScore, chantScore);

      const grace = s === 'attempt1' ? TIMING.PARRY_TUTORIAL_GRACE_MS : 0;
      const windowResult = resolveParryWindow(
        spokenAt,
        [{ id: 'tutorial-bolt', impactAt: impactAt.current, parryable: true }],
        grace,
      );

      const inWindow = windowResult.hit !== null;
      // Attempt one is deliberately generous: recognising the *feel* of the
      // parry matters more than a clean score the very first time.
      const passesRecognition = s === 'attempt1' ? score.grade !== 'Fail' || gestureScore > 0.2 : score.grade !== 'Fail';
      const success = inWindow && passesRecognition;

      setAttempt({
        grade: success ? (s === 'attempt1' && score.grade === 'Fail' ? 'Weak' : score.grade) : 'Fail',
        gesture: gestureScore,
        chant: chantScore,
        timingError: Number.isFinite(windowResult.error) ? windowResult.error : 0,
        success,
        reason: success ? undefined : inWindow ? 'recognition' : 'timing',
      });

      if (!success) {
        sfx.fail();
        return;
      }

      // The moment the whole tutorial exists for.
      bolt.current = null;
      resolveAt.current = 0;
      bus.projectiles = [];
      bus.circle.burstAt = performance.now();
      triggerHitStop(0.12, 0.55, 0.22);
      kickShake(1.2);
      pushImpact({
        x: 0,
        y: 1.55,
        z: bus.selfSide * (COMBAT.DUEL_DISTANCE / 2) - bus.selfSide * 2.2,
        color: current.visuals.color,
        kind: 'reflect',
        amount: 26,
        life: 0.85,
      });
      sfx.parry(score.grade === 'Fail' ? 'Weak' : score.grade);

      if (s === 'attempt1') {
        setStage('success1');
        window.setTimeout(() => {
          setAttempt(null);
          setStage('attempt2');
          launchBolt(13);
        }, 2600);
      } else {
        setStage('done');
      }
    },
    [launchBolt, typedChant, useTyping],
  );

  useEffect(() => {
    const beginCast = () => {
      const s = stageRef.current;
      if (casting.current || (s !== 'attempt1' && s !== 'attempt2')) return;
      casting.current = true;
      // Without a microphone the key-down is the honest utterance start.
      castOnset.current = useTyping || !mic.granted ? SpellNet.now() : 0;
      transcript.current = '';
      combatRecorder.begin();
      const current = spellRef.current;
      bus.casting = true;
      bus.castColor = current?.visuals.color ?? '#8fd8ff';
      bus.circle.active = true;
      bus.circle.progress = 0.2;
      bus.circle.color = current?.visuals.color ?? '#8fd8ff';
      bus.circle.complexity = current?.visuals.circleComplexity ?? 2;
      gestureTrail.color = current?.visuals.color ?? '#8fd8ff';
      sfx.circle();
    };

    const endCast = () => {
      if (!casting.current) return;
      casting.current = false;
      const points = combatRecorder.finish();
      bus.casting = false;
      bus.circle.active = false;
      gestureTrail.active = false;
      judge(points, castOnset.current || SpellNet.now());
    };

    const onMouseMove = (event: MouseEvent) => {
      if (!casting.current) return;
      combatRecorder.addDelta(event.movementX, event.movementY);
      syncTrailFrom(combatRecorder);
      bus.circle.progress = Math.min(1, 0.2 + combatRecorder.points.length / 90);
    };

    const breakStroke = () => {
      if (!casting.current) return;
      combatRecorder.endStroke();
      syncTrailFrom(combatRecorder);
    };

    const onMouseDown = (event: MouseEvent) => {
      if (event.button === 2) {
        event.preventDefault();
        beginCast();
      } else if (event.button === 0 && casting.current) {
        // Lift the pen: start a new stroke of the same sigil.
        event.preventDefault();
        breakStroke();
      }
    };
    const onMouseUp = (event: MouseEvent) => {
      if (event.button === 2) endCast();
    };
    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')) return;
      if (event.code === 'ShiftLeft' || event.code === 'ShiftRight') {
        breakStroke();
      } else if (event.code === 'Space' && !event.repeat) {
        event.preventDefault();
        beginCast();
      }
    };
    const onKeyUp = (event: KeyboardEvent) => {
      if (event.code === 'Space') endCast();
    };
    const onContextMenu = (event: MouseEvent) => event.preventDefault();

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mousedown', onMouseDown);
    window.addEventListener('mouseup', onMouseUp);
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    window.addEventListener('contextmenu', onContextMenu);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('mouseup', onMouseUp);
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('contextmenu', onContextMenu);
    };
  }, [judge, useTyping]);

  /* --- staging --------------------------------------------------------- */
  const start = useCallback(() => {
    sfx.unlock();
    setStage('incoming');
    bus.timeScale = 1;
    launchBolt(8);
  }, [launchBolt]);

  const onCraftSubmit = useCallback(
    async (result: CraftResult) => {
      setBuilding(true);
      try {
        const built = await buildSpellLocally({
          ...result,
          type: 'parry',
          ownerId: 'local',
          id: makeId('parry'),
        });
        setSpell(built.spell);
        setNotes(built.notes);
        setStage('ready');
        bus.timeScale = 1;
      } catch (err) {
        setNotes([err instanceof Error ? err.message : String(err)]);
      } finally {
        setBuilding(false);
      }
    },
    [],
  );

  const armFirstAttempt = useCallback(() => {
    setStage('attempt1');
    bus.selfHp = COMBAT.MAX_HP;
    launchBolt(8);
  }, [launchBolt]);

  const finish = useCallback(() => {
    if (spell) completeTutorial(spell);
  }, [completeTutorial, spell]);

  /* --- render ---------------------------------------------------------- */
  return (
    <div className="overlay">
      {stage === 'intro' ? (
        <div className="screen">
          <div className="panel" style={{ width: 'min(560px, 94vw)' }}>
            <h1 className="title">첫 번째 시련</h1>
            <p className="subtitle">
              대련장에 들어섰습니다. 상대는 이미 지팡이를 들고 있습니다.
              <br />
              마이크를 허용하면 목소리로 영창할 수 있고, 거부해도 키보드로 진행할 수 있습니다.
            </p>
            <button type="button" className="btn btn--primary" data-testid="tutorial-start" onClick={start}>
              시작
            </button>
          </div>
        </div>
      ) : null}

      {stage === 'frozen' ? (
        <div className="screen" style={{ background: 'rgba(6,8,16,0.55)' }}>
          <div className="panel fade-in" style={{ width: 'min(560px, 94vw)', textAlign: 'center' }}>
            <h2 className="title" style={{ fontSize: 26 }}>큰일이야!</h2>
            <p className="subtitle" style={{ fontSize: 15 }}>
              이 공격은 지팡이만 휘둘러서는 막을 수 없어.
              <br />
              너만의 패링 주문을 만들어보자!
            </p>
          </div>
        </div>
      ) : null}

      {stage === 'craft' ? (
        <div className="screen">
          <SpellCraft
            type="parry"
            title="패링 주문 제작"
            submitting={building}
            defaults={DEFAULTS}
            intro={
              <p className="subtitle">
                모든 패링 주문은 타이밍만 맞으면 공격을 막습니다. 당신이 정하는 것은 <b>막은 다음에 일어날 일</b>입니다.
                <br />
                반사, 마나 흡수, 공격 저장, 순간이동, 상대 기절, 다음 주문 강화, 완전 제거 — 원하는 대로 적으세요.
              </p>
            }
            onSubmit={(r) => void onCraftSubmit(r)}
          />
        </div>
      ) : null}

      {stage === 'ready' && spell ? (
        <div className="screen">
          <div className="panel fade-in" style={{ width: 'min(600px, 94vw)' }}>
            <h2 className="title" style={{ fontSize: 24 }}>주문 등록 완료</h2>
            <div className="interpretation" data-testid="tutorial-summary">
              {spell.summary}
            </div>
            {notes.length > 0 ? (
              <div className="hint" style={{ marginTop: 10 }}>
                {notes.map((n, i) => (
                  <div key={i}>· {n}</div>
                ))}
              </div>
            ) : null}
            <div className="section-label">시전 방법</div>
            <p className="hint">
              1. <b>스페이스</b> 또는 <b>마우스 오른쪽 버튼</b>을 누른 채로 유지합니다.
              <br />
              2. 등록한 영어 영창 “<b>{spell.chant}</b>”을 외칩니다 (아래 칸에 입력해도 됩니다).
              <br />
              3. 누른 채로 마우스/트랙패드를 움직여 등록한 문양을 그립니다. 획을 나눌 때는{' '}
              <b>좌클릭</b> 또는 <b>Shift</b>로 펜을 뗍니다.
              <br />
              4. 공격이 닿기 <b>직전</b>에 손을 뗍니다.
            </p>
            <input
              className="field"
              data-testid="tutorial-typed-chant"
              placeholder={
                useTyping ? '영창 입력 (음성 대신 사용)' : '영창 입력 (음성이 안 될 때 대신 사용)'
              }
              value={typedChant}
              onChange={(e) => setTypedChant(e.target.value)}
            />
            <div className="row" style={{ marginTop: 14 }}>
              <button type="button" className="btn btn--primary" data-testid="tutorial-arm" onClick={armFirstAttempt}>
                다시 공격이 온다 — 준비
              </button>
              {!micReady ? <span className="hint">마이크 없이 진행합니다 (키보드 영창).</span> : null}
            </div>
          </div>
        </div>
      ) : null}

      {(stage === 'attempt1' || stage === 'attempt2') && spell ? (
        <div className="hud">
          {/* The same indicator the match uses. Teaching the beat against a
              different one would be worse than not teaching it. */}
          <ParryTiming />
          <div className="hud__chant">
            <div className="chant-text">{useTyping ? typedChant : transcript.current}</div>
            <div className="chant-target">{spell.chant}</div>
            <input
              className="field fallback-input"
              data-testid="tutorial-typed-chant-live"
              placeholder="영창 입력 (음성 대신 사용)"
              value={typedChant}
              onChange={(e) => setTypedChant(e.target.value)}
            />
          </div>
          <div className="hud__announce" style={{ fontSize: 20, top: '14%' }}>
            {stage === 'attempt1' ? '연습 — 판정이 넉넉합니다' : '정상 속도 — 마지막 관문'}
          </div>
          {attempt ? (
            <div className="hud__grade" key={`${attempt.grade}-${attempt.timingError}`}>
              <div className={`grade-word grade-word--${attempt.success ? attempt.grade : 'Fail'}`}>
                {attempt.success ? `PARRY ${attempt.grade}` : 'FAIL'}
              </div>
              <div className="grade-detail">
                {attempt.reason === 'timing'
                  ? '타이밍이 맞지 않았습니다 — 공격이 닿기 직전에 시전하세요'
                  : attempt.reason === 'missed'
                    ? '공격에 맞았습니다. 다시 옵니다.'
                    : attempt.reason === 'recognition'
                      ? '영창이나 문양을 알아보지 못했습니다'
                      : `영창 ${Math.round(attempt.chant * 100)}% · 문양 ${Math.round(attempt.gesture * 100)}% · 타이밍 ${Math.round(attempt.timingError)}ms`}
              </div>
            </div>
          ) : null}
        </div>
      ) : null}

      {stage === 'success1' ? (
        <div className="screen" style={{ background: 'rgba(6,8,16,0.4)' }}>
          <div className="panel fade-in" style={{ width: 'min(520px, 94vw)', textAlign: 'center' }}>
            <h2 className="title" style={{ fontSize: 24 }}>막아냈다</h2>
            <p className="subtitle">{spell?.summary}</p>
            <p className="hint">이번엔 정상 속도입니다. 준비하세요.</p>
          </div>
        </div>
      ) : null}

      {stage === 'done' ? (
        <div className="screen">
          <div className="panel fade-in" style={{ width: 'min(560px, 94vw)', textAlign: 'center' }}>
            <h2 className="title">튜토리얼 완료</h2>
            <p className="subtitle">
              패링 주문 <b>{spell?.chant}</b>이 브라우저에 저장되었습니다. 다음 접속부터는 바로 로비로 갑니다.
            </p>
            <button type="button" className="btn btn--primary" data-testid="tutorial-finish" onClick={finish}>
              온라인 로비로
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
